import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import usuarios, {
  UsuarioCreateRequest,
  UsuarioUpdateRequest,
} from "../../api/usuarios";
import { errorToString } from "../../helpers/funcoes";
import { Titulo } from "../Titulo";

const PERFIS_DISPONIVEIS = ["admin", "professor"];

export function UsuariosForm() {
  const navigate = useNavigate();
  const { usuario: usuarioParam } = useParams();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [perfis, setPerfis] = useState<string[]>([]);

  const isEditing = !!usuarioParam;

  useEffect(() => {
    if (usuarioParam) {
      setLoading(true);
      usuarios
        .getUsuario(usuarioParam)
        .then((res) => {
          setUsuario(res.usuario);
          setPerfis(res.perfis);
        })
        .catch((err) => setError(errorToString(err)))
        .finally(() => setLoading(false));
    }
  }, [usuarioParam]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSaving(true);
    setError(undefined);

    if (isEditing) {
      const payload: UsuarioUpdateRequest = {
        perfis,
      };

      // Only include password if it was provided
      if (senha.trim()) {
        payload.senha = senha;
      }

      usuarios
        .updateUsuario(usuarioParam as string, payload)
        .then(() => navigate("/usuarios"))
        .catch((err) => setError(errorToString(err)))
        .finally(() => setSaving(false));
    } else {
      const payload: UsuarioCreateRequest = {
        usuario,
        senha,
        perfis,
      };

      usuarios
        .createUsuario(payload)
        .then(() => navigate("/usuarios"))
        .catch((err) => setError(errorToString(err)))
        .finally(() => setSaving(false));
    }
  };

  const onPerfilChange = (perfil: string, checked: boolean) => {
    if (checked) {
      setPerfis([...perfis, perfil]);
    } else {
      setPerfis(perfis.filter((p) => p !== perfil));
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Form onSubmit={onSubmit}>
      <Titulo text={`${isEditing ? "Editar" : "Adicionar"} Usuário`} />
      {error && (
        <Alert color="danger" data-cy="alert">
          {error}
        </Alert>
      )}
      <FormGroup row>
        <Label for="txtUsuario" sm={2}>
          Usuário:
        </Label>
        <Col sm={10}>
          <Input
            id="txtUsuario"
            data-cy="usuario"
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            required
            disabled={saving || isEditing}
            placeholder="Nome do usuário"
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="txtSenha" sm={2}>
          {isEditing ? "Nova Senha:" : "Senha:"}
        </Label>
        <Col sm={10}>
          <Input
            id="txtSenha"
            data-cy="senha"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required={!isEditing}
            disabled={saving}
            placeholder={
              isEditing
                ? "Deixe em branco para manter a senha atual"
                : "Senha do usuário"
            }
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label sm={2}>Perfis:</Label>
        <Col sm={10}>
          {PERFIS_DISPONIVEIS.map((perfil) => (
            <FormGroup key={perfil} check inline>
              <Input
                id={`chkPerfil${perfil}`}
                type="checkbox"
                checked={perfis.includes(perfil)}
                onChange={(e) => onPerfilChange(perfil, e.target.checked)}
                disabled={saving}
              />
              <Label for={`chkPerfil${perfil}`} check>
                {perfil === "admin" ? "Administrador" : "Professor"}
              </Label>
            </FormGroup>
          ))}
        </Col>
      </FormGroup>
      <Row>
        <Col sm={6}>{saving && <Spinner />}</Col>
        <Col sm={6} className="text-end">
          <Button
            type="button"
            color="secondary"
            className="me-2"
            onClick={() => navigate("/usuarios")}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={saving || perfis.length === 0}>
            Salvar
          </Button>
        </Col>
      </Row>
    </Form>
  );
}
